/**
 * Session status strip (task 20.3 / 20.4): the "Rufus is writing…" indicator
 * (task 14.5 WRITING_INDICATOR_PUSH_TYPE), the in-character cold-start waiting
 * cue ("Rufus consults the archives…", R15.2), and the clean retry state
 * (R13.5). The retry banner surfaces only the player-safe `reason` the server
 * supplies — never a raw error or stack trace — with clear "try again" /
 * "dismiss" affordances.
 */

import type { ConnectionStatus } from "../ws-client.js";
import type { RetryState } from "../useCampaignSession.js";

export interface SessionStatusProps {
  readonly writing: boolean;
  /**
   * The in-character cold-start waiting message ("Rufus consults the archives…"),
   * or null. Rendered as a transient cue distinct from the writing indicator
   * while an Aurora cold start resumes (R15.2).
   */
  readonly coldStartWaiting?: string | null;
  readonly retry: RetryState | null;
  readonly status: ConnectionStatus;
  /** Retry the failed turn (the clear call to action, R13.5). */
  readonly onRetry?: (retry: RetryState) => void;
  /** Dismiss the retry banner without retrying. */
  readonly onRetryAcknowledge?: () => void;
}

export function SessionStatus({
  writing,
  coldStartWaiting,
  retry,
  status,
  onRetry,
  onRetryAcknowledge,
}: SessionStatusProps) {
  return (
    <div className="session-status">
      {/*
        The transient cues are POLITE announcements grouped in one status
        region; the retry banner is a separate ASSERTIVE alert OUTSIDE it, so an
        alert is never nested in a polite live region (which produces ambiguous
        double/mixed-politeness announcements in some AT).
      */}
      <div className="session-cues" role="status" aria-live="polite">
        {status !== "open" ? (
          <span className="conn-status" data-testid="conn-status">
            {status === "connecting" ? "Connecting…" : "Reconnecting…"}
          </span>
        ) : null}

        {coldStartWaiting ? (
          <span
            className="cold-start-waiting"
            data-testid="cold-start-waiting"
          >
            {coldStartWaiting}
          </span>
        ) : null}

        {writing ? (
          <span className="writing-indicator" data-testid="writing-indicator">
            Rufus is writing…
          </span>
        ) : null}
      </div>

      {retry ? (
        <div className="retry-banner" role="alert" data-testid="retry-banner">
          <span>Something interrupted the last turn. {retry.reason}</span>
          {onRetry ? (
            <button
              type="button"
              data-testid="retry-try-again"
              onClick={() => onRetry(retry)}
            >
              Try again
            </button>
          ) : null}
          {onRetryAcknowledge ? (
            <button type="button" onClick={onRetryAcknowledge}>
              Dismiss
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
